import type { McpServerDto, McpTestResult } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, PlugZap } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/error-alert';
import { changedFields, FieldErrors, useToolCapabilities } from '@/components/resource-form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';

type Transport = McpServerDto['transport'];

const json = (value: unknown) => JSON.stringify(value, null, 2);
const refsText = (refs: Record<string, unknown>) =>
  Object.keys(refs).length > 0 ? json(refs) : '';
const parseRefs = (text: string): Record<string, unknown> =>
  text.trim() ? (JSON.parse(text) as Record<string, unknown>) : {};

/** "github · https://…" style target: the URL, or the command and its arguments. */
export const mcpTarget = (server: McpServerDto) =>
  server.transport === 'stdio'
    ? [server.command, ...server.args].filter(Boolean).join(' ')
    : (server.url ?? '');

/**
 * Adds an MCP server (no `server`) or edits one. Name and transport are fixed once created;
 * `readOnly` shows the server without saving (plugin servers, viewers).
 */
export function McpServerFormDialog({
  tenantId,
  server,
  readOnly = false,
  open,
  onOpenChange,
  trigger,
}: {
  tenantId: string;
  server: McpServerDto | null;
  readOnly?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {/* Content unmounts when closed, so the form starts from the server each time it opens. */}
        <McpServerForm
          tenantId={tenantId}
          server={server}
          readOnly={readOnly}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function McpServerForm({
  tenantId,
  server,
  readOnly,
  onDone,
}: {
  tenantId: string;
  server: McpServerDto | null;
  readOnly: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const caps = useToolCapabilities(tenantId);
  const editing = server !== null;
  const [transport, setTransport] = useState<Transport>(server?.transport ?? 'streamable_http');
  const [name, setName] = useState(server?.name ?? '');
  const [description, setDescription] = useState(server?.description ?? '');
  const [url, setUrl] = useState(server?.url ?? '');
  const [headers, setHeaders] = useState(
    server ? refsText(server.headers) : '{\n  "Authorization": { "env": "MCP_TOKEN" }\n}',
  );
  const [command, setCommand] = useState(server?.command ?? '');
  const [args, setArgs] = useState(server ? server.args.join('\n') : '');
  const [env, setEnv] = useState(server ? refsText(server.env) : '');
  const [filter, setFilter] = useState(server ? server.toolFilter.join(', ') : '');
  const [localError, setLocalError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      server
        ? api<McpServerDto>(`/mcp-servers/${server.id}`, { method: 'PATCH', body: payload })
        : api<McpServerDto>('/mcp-servers', { method: 'POST', body: payload }),
    onSuccess: (s) => {
      toast.success(editing ? `MCP server ${s.name} saved` : `MCP server ${s.name} added`);
      void queryClient.invalidateQueries({ queryKey: ['mcp-servers', tenantId] });
      onDone();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    const toolFilter = filter
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    let fields: Record<string, unknown>;
    try {
      fields =
        transport === 'streamable_http'
          ? { description, toolFilter, url, headers: parseRefs(headers) }
          : {
              description,
              toolFilter,
              command,
              args: args.split('\n').filter(Boolean),
              env: parseRefs(env),
            };
    } catch {
      setLocalError(
        `${transport === 'stdio' ? 'Environment' : 'Headers'} must be valid JSON: { "NAME": { "env": "KEY" } } or { "NAME": { "value": "text" } }.`,
      );
      return;
    }
    setLocalError(null);
    if (!server) {
      save.mutate({ name, transport, ...fields });
      return;
    }
    const changes = changedFields(fields, {
      description: server.description,
      toolFilter: server.toolFilter,
      url: server.url,
      headers: server.headers,
      command: server.command,
      args: server.args,
      env: server.env,
    });
    if (Object.keys(changes).length === 0) {
      onDone();
      return;
    }
    save.mutate(changes);
  };

  const stdioAllowed = (caps.data?.mcpStdioAllowlist.length ?? 0) > 0;
  const commands = [
    ...new Set([
      ...(caps.data?.mcpStdioAllowlist ?? []),
      ...(server?.command ? [server.command] : []),
    ]),
  ];
  const refHelp = (
    <>
      Each value is <code>{'{ "value": "…" }'}</code>, <code>{'{ "env": "KEY" }'}</code> or{' '}
      <code>{'{ "template": "Bearer {{env.KEY}}" }'}</code>.
      {editing &&
        ` Values shown as •••• are kept as they are, unless you change the ${
          transport === 'stdio' ? 'command or arguments' : 'URL'
        }: then re-enter them.`}
    </>
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {!server ? 'Add an MCP server' : readOnly ? 'View MCP server' : 'Edit MCP server'}
        </DialogTitle>
        <DialogDescription>
          {server?.pluginId && readOnly
            ? 'Installed by a plugin. Change it by updating the plugin.'
            : 'Its tools become available to agents you attach it to. Put credentials in environment variables and reference them.'}
        </DialogDescription>
      </DialogHeader>
      <form className="grid gap-4" onSubmit={submit} noValidate>
        <ErrorAlert error={save.error} />
        <FieldErrors error={save.error} />
        <fieldset disabled={readOnly} className="grid min-w-0 gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="mcp-name">Name</Label>
              <Input
                id="mcp-name"
                className="font-mono"
                value={name}
                readOnly={editing}
                onChange={(e) => setName(e.target.value)}
                placeholder="github"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mcp-transport">Transport</Label>
              <Select
                value={transport}
                onValueChange={(v) => setTransport(v as Transport)}
                disabled={editing || readOnly}
              >
                <SelectTrigger id="mcp-transport" aria-label="Transport">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="streamable_http">Streamable HTTP</SelectItem>
                  <SelectItem value="stdio" disabled={!stdioAllowed && transport !== 'stdio'}>
                    Local process (stdio){stdioAllowed ? '' : ' — not enabled'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {editing && !readOnly && (
            <p className="text-muted-foreground -mt-2 text-xs">
              Name and transport can’t be changed. Add a new server instead.
            </p>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="mcp-description">Description (optional)</Label>
            <Input
              id="mcp-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Read and search the team’s GitHub repositories"
            />
          </div>
          {transport === 'streamable_http' ? (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="mcp-url">URL</Label>
                <Input
                  id="mcp-url"
                  className="font-mono text-xs"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://mcp.example.com/mcp"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="mcp-headers">Headers (JSON)</Label>
                <Textarea
                  id="mcp-headers"
                  className="font-mono text-xs"
                  value={headers}
                  onChange={(e) => setHeaders(e.target.value)}
                  aria-describedby="mcp-headers-help"
                />
                <p id="mcp-headers-help" className="text-muted-foreground text-xs">
                  {refHelp}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="mcp-command">Command</Label>
                <Select value={command} onValueChange={setCommand} disabled={readOnly}>
                  <SelectTrigger id="mcp-command" aria-label="Command">
                    <SelectValue placeholder="Choose an allowed command" />
                  </SelectTrigger>
                  <SelectContent>
                    {commands.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="mcp-args">Arguments (one per line)</Label>
                <Textarea
                  id="mcp-args"
                  className="font-mono text-xs"
                  value={args}
                  onChange={(e) => setArgs(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="mcp-env">Environment (JSON, optional)</Label>
                <Textarea
                  id="mcp-env"
                  className="font-mono text-xs"
                  value={env}
                  onChange={(e) => setEnv(e.target.value)}
                  placeholder={'{\n  "GITHUB_TOKEN": { "env": "GITHUB_TOKEN" }\n}'}
                  aria-describedby="mcp-env-help"
                />
                <p id="mcp-env-help" className="text-muted-foreground text-xs">
                  {refHelp}
                </p>
              </div>
            </>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="mcp-filter">Only these tools (comma-separated, optional)</Label>
            <Input id="mcp-filter" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
        </fieldset>
        {localError && <p className="text-destructive text-sm">{localError}</p>}
        <DialogFooter>
          {readOnly ? (
            <Button type="button" variant="outline" onClick={onDone}>
              Close
            </Button>
          ) : (
            <Button type="submit" disabled={save.isPending}>
              {editing ? 'Save changes' : 'Add server'}
            </Button>
          )}
        </DialogFooter>
      </form>
    </>
  );
}

/** How many tool names the list shows before "+N more". */
const VISIBLE_TOOLS = 24;

function McpToolList({
  serverName,
  tools,
}: {
  serverName: string;
  tools: Array<{ name: string; description: string }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? tools : tools.slice(0, VISIBLE_TOOLS);
  const hidden = tools.length - shown.length;
  return (
    <div className="grid gap-2" data-testid={`mcp-tools-${serverName}`}>
      <p className="text-muted-foreground text-xs">
        {tools.length} tool{tools.length === 1 ? '' : 's'} available
      </p>
      <ul className="flex flex-wrap gap-1" aria-label={`Tools of ${serverName}`}>
        {shown.map((t) => (
          <li key={t.name}>
            <Badge variant="secondary" className="font-mono font-normal" title={t.description}>
              {t.name}
            </Badge>
          </li>
        ))}
        {(hidden > 0 || expanded) && tools.length > VISIBLE_TOOLS && (
          <li>
            <Button
              size="xs"
              variant="ghost"
              aria-expanded={expanded}
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? 'Show less' : `+${hidden} more`}
            </Button>
          </li>
        )}
      </ul>
    </div>
  );
}

/** An icon button that connects to the server and lists its tools in a dialog. */
export function McpTestDialog({ server }: { server: McpServerDto }) {
  const [open, setOpen] = useState(false);
  const test = useMutation({
    mutationFn: () => api<McpTestResult>(`/mcp-servers/${server.id}/test`, { method: 'POST' }),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) test.mutate();
        else test.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={`Test ${server.name}`}
          title="Test connection and list tools"
        >
          <PlugZap aria-hidden />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Test <span className="font-mono">{server.name}</span>
          </DialogTitle>
          <DialogDescription className="font-mono break-all">{mcpTarget(server)}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 text-sm" aria-live="polite">
          {test.isPending && (
            <p className="text-muted-foreground flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" aria-hidden /> Connecting…
            </p>
          )}
          <ErrorAlert error={test.error} />
          {test.data &&
            (test.data.ok ? (
              <McpToolList serverName={server.name} tools={test.data.tools} />
            ) : (
              <p className="text-destructive">{test.data.message}</p>
            ))}
          {server.toolFilter.length > 0 && (
            <p className="text-muted-foreground text-xs">
              Agents only get: <span className="font-mono">{server.toolFilter.join(', ')}</span>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending}>
            Test again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
